import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractDialogResultData, AbstractFormDialog, AbstractFormDialogData, DIALOG_RETURN_TYPES, FormDialogComponent } from '@next/core-lib/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, skip } from 'rxjs/operators';
import { CustomerOrderPlan, OrderPlanCropAidDisplayData, ProductStockData, SiteState } from 'src/app/shared/models';
import { isNegative } from 'src/app/shared/utils';

import { getCropAids, getPackages, autoSaveCustomerProductStock, autoSaveCustomerProductStockSuccess } from '../../store/actions';
import { selectCustomerOrderPlan } from '../../store/selectors';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { NgClass } from '@angular/common';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';

export interface CustomerProductInventoryDialogData extends AbstractFormDialogData {
  orderplanByCropAids: OrderPlanCropAidDisplayData[];
}

interface GbmProductWithFormIndex extends OrderPlanCropAidDisplayData {
  formIndex: number;
}

@Component({
    selector: 'app-customer-product-inventory-dialog',
    templateUrl: './customer-product-inventory-dialog.component.html',
    styleUrls: ['./customer-product-inventory-dialog.component.scss'],
    imports: [
        FormDialogComponent,
        FormsModule,
        ReactiveFormsModule,
        MatAccordion,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        NgClass,
        MatFormField,
        MatInput,
        TranslatePipe,
        AgrNumberPipe,
    ],
})
export class CustomerProductInventoryDialogComponent
  extends AbstractFormDialog<void>
  implements OnInit, OnDestroy {
  data = inject<CustomerProductInventoryDialogData>(MAT_DIALOG_DATA);
  private readonly formBuilder = inject(FormBuilder);
  protected readonly mdDialogRef = inject<MatDialogRef<CustomerProductInventoryDialogComponent, AbstractDialogResultData<void>>>(MatDialogRef);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly actions = inject(Actions);

  formGroup = this.formBuilder.group({
    products: this.formBuilder.array([]),
  });

  products: GbmProductWithFormIndex[] = [];
  private readonly destroy$ = new Subject<void>();
  private readonly expandedPanels = new Set<number>();

  readonly isNegative = isNegative;

  ngOnInit(): void {
    this.store.dispatch(getCropAids());
    this.store.dispatch(getPackages());
    this.initializeProducts();
    this.subscribeToOrderPlanUpdates();
    this.actions
      .pipe(ofType(autoSaveCustomerProductStockSuccess), takeUntil(this.destroy$))
      .subscribe(({ productInventories }) => {
        if (this.matchesCurrentInventory(productInventories)) {
          this.formGroup.markAsPristine();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToOrderPlanUpdates(): void {
    this.store
      .select(selectCustomerOrderPlan)
      .pipe(
        takeUntil(this.destroy$),
        skip(1),
      )
      .subscribe((orderPlan) => {
        this.updateProductsFromOrderPlan(orderPlan);
      });
  }

  private updateProductsFromOrderPlan(orderPlan: CustomerOrderPlan[]): void {
    const orderplanByCropAids = this.mapOrderPlanForUpdate(orderPlan);

    for (const updatedProduct of orderplanByCropAids) {
      const existingProduct = this.products.find((p) => p.cropAid.id === updatedProduct.cropAid.id);
      if (existingProduct) {
        existingProduct.orderLinesTotalAmount = updatedProduct.orderLinesTotalAmount;
        existingProduct.requisiteAmount = updatedProduct.requisiteAmount;
      }
    }
  }

  private mapOrderPlanForUpdate(orderPlan: CustomerOrderPlan[]): OrderPlanCropAidDisplayData[] {
    return orderPlan
      .filter((plan) => plan.isMainCropAid || !plan.isPackage)
      .map((plan) => {
        return {
          cropAid: {
            id: plan.cropAidId,
            name: plan.cropAidName,
            unitOfMeasurement: plan.unitOfMeasurement,
          },
          requisiteAmount: plan.isMainCropAid
            ? orderPlan.find((op) => op.cropAidId === plan.cropAidId && op.isPackage)?.requisiteAmount ?? 0
            : plan.requisiteAmount,
          customerProductInventoryAmount: plan.customerProductInventoryAmount,
          orderLinesTotalAmount: plan.orderLinesTotalAmount,
        } as unknown as OrderPlanCropAidDisplayData;
      })
      .filter((plan) => plan.requisiteAmount > 0);
  }

  private initializeProducts(): void {
    this.products = this.data.orderplanByCropAids.map((product, index) => ({
      ...product,
      formIndex: index,
    }));

    const productsFormArray = this.formGroup.get('products') as FormArray;
    productsFormArray.clear();

    for (const product of this.products) {
      const productFormGroup = this.formBuilder.group({
        cropAidId: [product.cropAid.id],
        stockAmount: [product.customerProductInventoryAmount, [Validators.required, Validators.min(0)]],
      });

      productsFormArray.push(productFormGroup);

      productFormGroup.get('stockAmount')?.valueChanges
        .pipe(
          takeUntil(this.destroy$),
          debounceTime(300),
          distinctUntilChanged(),
        )
        .subscribe((newValue) => {
          if (newValue !== null && newValue !== undefined) {
            this.updateProductStock(product.cropAid.id, newValue);
          }
        });
    }
  }

  private updateProductStock(cropAidId: number, newStockAmount: number): void {
    if (!Number.isFinite(newStockAmount) || newStockAmount < 0) {
      return;
    }

    const product = this.products.find((p) => p.cropAid.id === cropAidId);
    if (product) {
      product.customerProductInventoryAmount = newStockAmount;

      const productInventories: ProductStockData[] = this.products
        .filter((p) => Number.isFinite(p.customerProductInventoryAmount) && p.customerProductInventoryAmount >= 0)
        .map((p) => ({
          cropAidId: p.cropAid.id,
          amount: p.customerProductInventoryAmount,
        }));

      this.store.dispatch(autoSaveCustomerProductStock({ productInventories }));
    }
  }

  private matchesCurrentInventory(productInventories: ProductStockData[]): boolean {
    const currentInventory = this.products
      .map((product) => ({
        cropAidId: product.cropAid.id,
        amount: Number(this.getStockControl(product.formIndex).value),
      }))
      .filter((product) => Number.isFinite(product.amount) && product.amount >= 0);

    return (
      this.products.every((product) => {
        const control = this.getStockControl(product.formIndex);
        const amount = Number(control.value);
        return (Number.isFinite(amount) && amount >= 0) || !control.dirty;
      }) &&
      currentInventory.length === productInventories.length &&
      productInventories.every((submitted) =>
        currentInventory.some((current) => current.cropAidId === submitted.cropAidId && current.amount === submitted.amount),
      )
    );
  }

  get productsFormArray(): FormArray {
    return this.formGroup.get('products') as FormArray;
  }

  getStockControl(formIndex: number): FormControl {
    if (formIndex >= this.productsFormArray.length) {
      return new FormControl(0);
    }
    const productFormGroup = this.productsFormArray.at(formIndex);
    return productFormGroup?.get('stockAmount') as FormControl;
  }

  getDifference(requisiteAmount: number, stockAmount: number, orderAmount: number): number {
    return stockAmount + orderAmount - requisiteAmount;
  }

  onPanelOpened(cropAidId: number): void {
    this.expandedPanels.add(cropAidId);
  }

  onPanelClosed(cropAidId: number): void {
    this.expandedPanels.delete(cropAidId);
  }

  isPanelExpanded(cropAidId: number): boolean {
    return this.expandedPanels.has(cropAidId);
  }

  override cancel(result?: void) {
    // Since all changes are auto-saved, we can skip the pending changes check
    // Call the base AbstractDialog.cancel directly, not AbstractFormDialog.cancel
    this.mdDialogRef.close({ status: DIALOG_RETURN_TYPES.cancel, result });
    this.canceling = false;
  }

  process() {
    super.confirm();
  }
}
